<script lang="ts" module>
    export const route = "/signin";
    export const sessionRequired = false;
</script>

<script lang="ts">
    import * as Field from "../lib/components/ui/field/index.js";
    import { Input } from "../lib/components/ui/input/index.js";
    import { Button } from "../lib/components/ui/button/index.js";
    import icon from "../assets/icon-192x192.png";
    import { auth } from "../lib/auth.svelte.js";
    import { getClient } from "../lib/utils.js";
    import { useURL } from "../lib/url.svelte.js";
    import { m, resolveAuthError } from "../lib/messages.svelte.js";
    import type { Res } from "@repo/backend/client";
    import { BooleanAsStringSchema, z } from "@repo/utils";
    import * as InputOTP from "../lib/components/ui/input-otp/index.js";
    import { REGEXP_ONLY_DIGITS } from "bits-ui";
    import { toast } from "svelte-sonner";
    import { resolve } from "../lib/route.svelte.js";
    import { useLocalStorage } from "../lib/local-storage.svelte.js";

    let loading = $state(false);
    let policy = $state<Res["Get auth email policy"]>();
    let providerId = $derived(policy && "providerId" in policy ? policy.providerId : undefined);
    const url = useURL({ dest: z.string(), error: z.string(), admin: BooleanAsStringSchema });
    const ls = useLocalStorage("signin", { email: z.string(), otpStarted: z.boolean() });
    const email = $derived(ls.email ?? "");
    let otp = $state("");
    let otpTimeout = $state<number>();
    const previousError = $derived(url.query.error);
    let authWindow = $state<Window | null>(null);
    $effect(() => {
        if (previousError) {
            toast.error(resolveAuthError(previousError) ?? previousError, {
                duration: 10 * 1000,
                closeButton: true,
            });
        }
    });

    async function signIn() {
        if (!policy || "error" in policy) {
            return;
        }
        if (policy.providerId) {
            loading = true;
            if (window.__TURIAN_ENTRY__ === "workspace") {
                await auth.signIn.social({
                    provider: policy.providerId,
                    loginHint: email.toLowerCase(),
                    callbackURL: url.query.dest,
                    requestSignUp: policy.signUpEnabled,
                });
            } else {
                authWindow = window.open(
                    resolve(
                        "/oauth-popup",
                        {},
                        {
                            provider: policy.providerId,
                            loginHint: email.toLowerCase(),
                            requestSignUp: String(policy.signUpEnabled ?? false),
                        },
                    ),
                    "_blank",
                );
            }
        }
    }

    $effect(() => {
        if (authWindow?.closed === false) {
            const intervalId = window.setInterval(async () => {
                if (!authWindow || authWindow.closed) {
                    window.clearInterval(intervalId);
                    window.location.assign(url.query.dest ?? "/");
                }
            }, 1000);
        }
    });
</script>

<svelte:head>
    <script src="https://accounts.google.com/gsi/client" async></script>
</svelte:head>

    <form>
        <div class="bg-background flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10">
            <div class="w-full max-w-sm">
                <div class="flex flex-col gap-3">
                    <Field.Group>
                        <div class="flex flex-col items-center text-center">
                            <div class="flex items-center justify-center">
                                <enhanced:img src={icon} alt="Turian Logo" class="mx-auto mb-8 h-20 w-20" />
                            </div>
                            <h1 class="text-xl font-bold">{m["welcomeToTurian"]()}</h1>
                        </div>
                        <Field.Field>
                            {#if (policy && providerId === null && window.__TURIAN_ENTRY__ !== "workspace") || ls.otpStarted}
                                <InputOTP.Root
                                    maxlength={6}
                                    bind:value={
                                        () => otp,
                                        x => {
                                            otp = x;
                                            if (otp.length === 6) {
                                                window.clearTimeout(otpTimeout);
                                                otpTimeout = window.setTimeout(async () => {
                                                    loading = true;
                                                    const { error } = await auth.signIn.emailOtp({
                                                        email: email.toLowerCase(),
                                                        otp,
                                                    });
                                                    delete ls.otpStarted;
                                                    if (error) {
                                                        toast.error(error.message ?? JSON.stringify(error));
                                                        otp = "";
                                                    } else {
                                                        window.location.replace(url.query.dest ?? "/");
                                                    }
                                                    loading = false;
                                                }, 1000);
                                            }
                                        }
                                    }
                                    pattern={REGEXP_ONLY_DIGITS}
                                    class="justify-center"
                                >
                                    {#snippet children({ cells })}
                                        <InputOTP.Group>
                                            {#each cells as cell, i (i)}
                                                <InputOTP.Slot {cell} />
                                            {/each}
                                        </InputOTP.Group>
                                    {/snippet}
                                </InputOTP.Root>
                            {:else}
                                <Input
                                    id="email"
                                    type="email"
                                    bind:value={
                                        () => email,
                                        x => {
                                            ls.email = x;
                                            policy = undefined;
                                        }
                                    }
                                    disabled={loading}
                                    placeholder={m["enterYourEmail"]()}
                                    required
                                />
                            {/if}
                        </Field.Field>
                        <Field.Field class="items-center">
                            {#if providerId === "google"}
                                <Button
                                    type="submit"
                                    variant="outline"
                                    class="w-full gap-2"
                                    disabled={loading}
                                    onclick={signIn}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="1em"
                                        height="1em"
                                        viewBox="0 0 256 262"
                                    >
                                        <path
                                            fill="#4285F4"
                                            d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622l38.755 30.023l2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"
                                        ></path>
                                        <path
                                            fill="#34A853"
                                            d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055c-34.523 0-63.824-22.773-74.269-54.25l-1.531.13l-40.298 31.187l-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"
                                        ></path>
                                        <path
                                            fill="#FBBC05"
                                            d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82c0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602z"
                                        ></path>
                                        <path
                                            fill="#EB4335"
                                            d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0C79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"
                                        ></path>
                                    </svg>
                                    {m["continueWithGoogle"]()}
                                </Button>
                            {:else if providerId === "microsoft"}
                                <Button
                                    type="submit"
                                    variant="outline"
                                    class="w-full gap-2"
                                    disabled={loading}
                                    onclick={signIn}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="1em"
                                        height="1em"
                                        viewBox="0 0 24 24"
                                    >
                                        <path fill="#00A4EF" d="M2 3h9v9H2zm9 19H2v-9h9zM21 3v9h-9V3zm0 19h-9v-9h9z"
                                        ></path>
                                    </svg>
                                    {m["continueWithMicrosoft"]()}
                                </Button>
                            {:else if providerId === "hubspot"}
                                <Button
                                    type="submit"
                                    variant="outline"
                                    class="w-full gap-2"
                                    disabled={loading}
                                    onclick={signIn}
                                >
                                    <svg
                                        role="img"
                                        width="1em"
                                        height="1em"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fill="#FF7A59"
                                            d="M18.164 7.93V5.084a2.198 2.198 0 001.267-1.978v-.067A2.2 2.2 0 0017.238.845h-.067a2.2 2.2 0 00-2.193 2.193v.067a2.196 2.196 0 001.252 1.973l.013.006v2.852a6.22 6.22 0 00-2.969 1.31l.012-.01-7.828-6.095A2.497 2.497 0 104.3 4.656l-.012.006 7.697 5.991a6.176 6.176 0 00-1.038 3.446c0 1.343.425 2.588 1.147 3.607l-.013-.02-2.342 2.343a1.968 1.968 0 00-.58-.095h-.002a2.033 2.033 0 102.033 2.033 1.978 1.978 0 00-.1-.595l.005.014 2.317-2.317a6.247 6.247 0 104.782-11.134l-.036-.005zm-.964 9.378a3.206 3.206 0 113.215-3.207v.002a3.206 3.206 0 01-3.207 3.207z"
                                        />
                                    </svg>
                                    {m["continueWithHubspot"]()}
                                </Button>
                            {:else if providerId && policy && policy.isSso}
                                <Button
                                    type="submit"
                                    variant="outline"
                                    class="w-full gap-2"
                                    disabled={loading}
                                    onclick={async () => {
                                        if (!policy || "error" in policy) {
                                            return;
                                        }
                                        loading = true;
                                        if (window.__TURIAN_ENTRY__ === "workspace") {
                                            auth.signIn.sso({
                                                providerId: providerId,
                                                loginHint: email.toLowerCase(),
                                                callbackURL: url.query.dest ?? resolve("/"),
                                                errorCallbackURL: resolve("/error"),
                                                requestSignUp: policy.signUpEnabled,
                                            });
                                        } else {
                                            authWindow = window.open(
                                                resolve(
                                                    "/oauth-popup",
                                                    {},
                                                    {
                                                        sso: String(true),
                                                        provider: providerId,
                                                        loginHint: email.toLowerCase(),
                                                        requestSignUp: String(policy.signUpEnabled ?? false),
                                                    },
                                                ),
                                                "_blank",
                                            );
                                        }
                                    }}
                                >
                                    {m["continueWithSSO"]()}
                                </Button>
                            {:else if providerId === null || ls.otpStarted}
                                {#if window.__TURIAN_ENTRY__ === "workspace"}
                                    {m["Check your inbox at {email} for the sign-in link."]({ email })}
                                {:else}
                                    <div class="flex flex-col items-center gap-2">
                                        <div>{m["Check your inbox at {email} for the code."]({ email })}</div>
                                        <Button
                                            variant="secondary"
                                            onclick={() => {
                                                otp = "";
                                                delete ls.otpStarted;
                                                policy = undefined;
                                            }}>{m["Cancel"]()}</Button
                                        >
                                    </div>
                                {/if}
                            {:else}
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    onclick={async () => {
                                        loading = true;
                                        try {
                                            const r = await getClient()["Get auth email policy"]({
                                                query: { email: email.toLowerCase() },
                                            });
                                            if (!("error" in r) && r.providerId === null) {
                                                if (window.__TURIAN_ENTRY__ === "workspace") {
                                                    const { error } = await auth.signIn.magicLink({
                                                        email: email.toLowerCase(),
                                                        callbackURL: url.query.dest,
                                                        errorCallbackURL: url.raw,
                                                    });
                                                    if (error) {
                                                        toast.error(JSON.stringify(error));
                                                    }
                                                } else {
                                                    const { error } = await auth.emailOtp.sendVerificationOtp({
                                                        email: email.toLowerCase(),
                                                        type: "sign-in",
                                                    });
                                                    if (error) {
                                                        toast.error(JSON.stringify(error));
                                                    } else {
                                                        ls.otpStarted = true;
                                                    }
                                                }
                                            }
                                            policy = r;
                                        } finally {
                                            loading = false;
                                        }
                                    }}>{m["continue"]()}</Button
                                >
                            {/if}
                        </Field.Field>
                    </Field.Group>
                </div>
            </div>
        </div>
    </form>
